import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { nexusApi, useNexusAuth } from "@/context/NexusAuthContext";
import { STATUS_META } from "@/pages/nexus/meta";
import "@/pages/nexus/nexus.css";

const STAGE_LABELS = {
  new: "New", nda_pending: "NDA Pending", pack_sent: "Pack Sent",
  in_discussion: "In Discussion", introduced: "Introduced", closed: "Closed", lost: "Lost",
};

export default function NexusAccount() {
  const { buyer, loading } = useNexusAuth() || {};
  const nav = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [ndas, setNdas] = useState([]);

  useEffect(() => {
    if (loading) return;
    if (!buyer) { nav("/nexus/signin"); return; }
    nexusApi.get("/me/enquiries").then((r) => setEnquiries(r.data.items || [])).catch(() => {});
    nexusApi.get("/me/ndas").then((r) => setNdas(r.data.items || [])).catch(() => {});
  }, [buyer, loading, nav]);

  if (loading || !buyer) return <div className="nx-container py-24 text-white/50 font-mono text-xs">Loading…</div>;

  return (
    <div data-testid="nexus-account">
      <section className="nx-hero" style={{ padding: "60px 0 30px" }}>
        <div className="nx-container">
          <div className="nx-eyebrow mb-3">Account</div>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-2">Welcome, {buyer.full_name?.split(" ")[0]}.</h1>
          <p className="text-white/60 text-sm">{buyer.email} · {buyer.buyer_type} · {buyer.qualified ? <span className="text-[#7FB08A]">Qualified</span> : "Qualification pending review"}</p>
        </div>
      </section>

      <section className="nx-container py-10 grid gap-8 md:grid-cols-3">
        <div className="md:col-span-2">
          <div className="nx-eyebrow mb-3">Enquiries</div>
          {enquiries.length === 0 ? (
            <div className="nx-card text-white/60">No enquiries yet. <Link to="/nexus/listings" className="text-[#C9896E]">Browse the marketplace →</Link></div>
          ) : (
            <div className="space-y-3">
              {enquiries.map((e) => (
                <Link
                  key={e.id}
                  to={`/nexus/listings/${e.listing_slug}`}
                  data-testid={`account-enq-${e.id}`}
                  className="nx-card block"
                >
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div className="font-serif text-lg text-white">{e.listing_title}</div>
                    <span className="nx-tag gold">{STAGE_LABELS[e.stage] || e.stage}</span>
                  </div>
                  <p className="text-white/70 text-sm line-clamp-2">{e.message}</p>
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/45">
                    {new Date(e.created_at).toLocaleString("en-GB")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <aside>
          <div className="nx-eyebrow mb-3">NDAs signed</div>
          {ndas.length === 0 ? (
            <div className="nx-card text-white/60 text-sm">No NDAs signed yet.</div>
          ) : (
            <div className="nx-card">
              <ul className="space-y-3">
                {ndas.map((n) => (
                  <li key={n.id} className="pb-3 border-b border-white/8 last:border-none">
                    <Link to={`/nexus/listings/${n.listing_slug}`} className="text-white hover:text-[#C9896E]">
                      {n.listing_slug}
                    </Link>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/45 mt-1">
                      Signed {new Date(n.signed_at).toLocaleDateString("en-GB")}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
